import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import { Bell, Trash, CheckCircle, Funnel } from '@phosphor-icons/react';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { navigateFromNotification } from '../../utils/notificationNavigation';
import useThemeStyles from '../../theme/useThemeStyles';

function NotificationsPage() {
  const styles = useThemeStyles();
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteAllNotifications,
    deleteNotification,
  } = useNotifications();

  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  useEffect(() => {
    // Fetch notifications when component mounts
    fetchNotifications(1, 50, filter === 'unread');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [filter]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    const result = await fetchNotifications(nextPage, 50, filter === 'unread');
    if (result && result.notifications.length < 50) {
      setHasMore(false);
    }
    setPage(nextPage);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }

    navigateFromNotification(navigate, notification, {
      isSupplier: user?.role === 'supplier',
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'unread') return !notification.isRead;
    if (filter === 'read') return notification.isRead;
    return true;
  });

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1);
    setHasMore(true);
    setFilterMenuAnchor(null);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          Notifications
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            aria-label={`Filter: ${filter === 'all' ? 'All' : filter === 'unread' ? 'Unread' : 'Read'}`}
          >
            <Funnel size={20} />
          </IconButton>
          {unreadCount > 0 && (
            <IconButton
              onClick={markAllAsRead}
              aria-label="Mark all as read"
            >
              <CheckCircle size={20} />
            </IconButton>
          )}
          {notifications.length > 0 && (
            <IconButton
              color="error"
              onClick={deleteAllNotifications}
              aria-label="Delete all notifications"
            >
              <Trash size={20} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleFilterChange('all')}>All</MenuItem>
        <MenuItem onClick={() => handleFilterChange('unread')}>
          Unread ({unreadCount})
        </MenuItem>
        <MenuItem onClick={() => handleFilterChange('read')}>Read</MenuItem>
      </Menu>

      {loading && notifications.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredNotifications.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Bell size={64} weight="regular" color="#ccc" />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            No notifications {filter !== 'all' ? `(${filter})` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {filter === 'unread'
              ? "You're all caught up!"
              : 'You will see notifications here when you receive them.'}
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {filteredNotifications.map((notification, _index) => (
            <ListItemButton
              key={notification._id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                p: 2,
                backgroundColor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                opacity: notification.isRead ? 0.4 : 1,
                transition: 'opacity 0.2s ease',
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              <ListItemText
                primary={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: notification.isRead ? 'normal' : 'bold',
                      }}
                    >
                      {notification.title}
                    </Typography>
                    {notification.priority && (
                      <Chip
                        label={notification.priority}
                        size="small"
                        color={getPriorityColor(notification.priority)}
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {formatTimeAgo(notification.createdAt)}
                    </Typography>
                  </Box>
                }
              />

              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, ml: 2, alignItems: 'center' }}>
                {!notification.isRead && (
                  <IconButton
                    size="medium"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await markAsRead(notification._id);
                    }}
                    aria-label="Mark notification as read"
                    sx={{ opacity: 0.8, p: 0.75, '&:hover': { opacity: 1 } }}
                  >
                    <CheckCircle size={20} />
                  </IconButton>
                )}
                <IconButton
                  size="medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNotification(notification._id);
                  }}
                  sx={{ opacity: 0.8, p: 0.75, '&:hover': { opacity: 1 } }}
                >
                  <Trash size={20} />
                </IconButton>
              </Box>
            </ListItemButton>
          ))}
        </List>
          )}

      {hasMore && filteredNotifications.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default NotificationsPage;

